import type { HighlightManager } from '../highlight-manager.class';

/**
 * Parameters for clearing a highlight
 */
export interface ClearHighlightParams {
	objectId?: string;
}

/**
 * Clear the current highlight with animation
 *
 * @param self The HighlightManager instance
 * @param params Clear parameters
 */
export const clearHighlightCfn = (
	self: HighlightManager,
	params: ClearHighlightParams = {}
): void => {
	const { objectId } = params;

	// Access fields via public getters
	const currentHighlightId = self.currentHighlightId;
	const highlights = self.highlights;
	const animationManager = self.animationManager;
	const animationDuration = self.animationDuration;
	const maxIntensity = self.maxIntensity;

	const idToClear = objectId ?? currentHighlightId;
	if (!idToClear) return;

	const highlight = highlights.get(idToClear);
	if (!highlight) return;

	// Animate back to source position with fading intensity
	const startPos = highlight.light?.position.clone() ?? highlight.highlightTarget;
	const endPos = highlight.highlightLightSource;

	self.animationPhase = 'backing-up';

	animationManager.animatePosition(
		`highlight-position-${highlight.objectId}`,
		startPos,
		endPos,
		animationDuration,
		(position) => {
			highlight.updateLightPosition(position);

			// Decrease intensity as we move away
			const distanceFromStart = position.distanceTo(startPos);
			const totalDistance = startPos.distanceTo(endPos);
			const progress = totalDistance > 0 ? distanceFromStart / totalDistance : 1;
			highlight.setIntensity(maxIntensity * (1 - progress));
		},
		{
			onComplete: () => {
				removeHighlight(self, idToClear);
				self.animationPhase = null;

				if (self.currentHighlightId === idToClear) {
					self.currentHighlightId = null;
				}
			}
		}
	);
};

/**
 * Remove a specific highlight (internal helper)
 */
const removeHighlight = (self: HighlightManager, objectId: string): void => {
	const highlights = self.highlights;
	const animationManager = self.animationManager;
	const onLastHighlightRemoved = self.onLastHighlightRemoved;

	const highlight = highlights.get(objectId);
	if (highlight) {
		animationManager.stop(`highlight-position-${highlight.objectId}`);
		animationManager.stop(`highlight-intensity-${highlight.objectId}`);
		highlight.dispose();
		highlights.delete(objectId);

		// Trigger callback when last highlight is removed
		if (highlights.size === 0) {
			onLastHighlightRemoved?.();
		}
	}
};
